import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { mermaidSvgToDataUrl } from '@/lib/mermaid'
import {
  closeMermaidLightbox,
  subscribeMermaidLightbox,
  type MermaidLightboxPayload,
} from '@/lib/mermaid-lightbox'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

/** Single app-wide Mermaid fullscreen overlay (TipTap DOM + markdown preview). */
export function MermaidLightboxHost() {
  const [payload, setPayload] = useState<MermaidLightboxPayload | null>(null)

  useEffect(() => subscribeMermaidLightbox(setPayload), [])

  const open = payload !== null && !!payload.svgHtml

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeMermaidLightbox()
      }}
    >
      <DialogContent
        data-slot="mermaid-lightbox"
        showCloseButton
        className={cn(
          '!fixed !inset-4 !translate-x-0 !translate-y-0 !rounded-lg !p-4 !gap-3',
          '!flex !flex-col bg-background/95 shadow-lg backdrop-blur-sm',
          'h-[calc(100dvh-2rem)] w-auto max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] sm:!max-w-[calc(100vw-2rem)]',
        )}
        data-testid={testIds.mermaid.lightbox}
        onEscapeKeyDown={() => closeMermaidLightbox()}
      >
        <DialogTitle className="sr-only">Diagram preview</DialogTitle>
        <DialogDescription className="sr-only">
          Drag to pan when zoomed. Scroll or pinch to zoom. Double-click resets. Press Escape to close.
        </DialogDescription>
        {payload?.svgHtml ? <MermaidLightboxBody svgHtml={payload.svgHtml} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function MermaidLightboxBody({ svgHtml }: { svgHtml: string }) {
  const dataUrl = useMemo(() => mermaidSvgToDataUrl(svgHtml), [svgHtml])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <TransformWrapper
        key={dataUrl.slice(0, 120)}
        centerOnInit
        initialScale={1}
        minScale={0.25}
        maxScale={8}
        limitToBounds={false}
        wheel={{ step: 0.12 }}
        pinch={{ disabled: false }}
        doubleClick={{ disabled: false, mode: 'reset', animationTime: 200 }}
      >
        <TransformComponent
          wrapperClass="!w-full grow min-h-[50vh]"
          contentClass="flex items-center justify-center"
        >
          <img
            src={dataUrl}
            alt="Mermaid diagram"
            className="mx-auto block max-h-[calc(100dvh-11rem)] w-auto max-w-full select-none object-contain rounded-sm"
            data-testid={testIds.mermaid.lightboxDiagram}
            draggable={false}
          />
        </TransformComponent>
      </TransformWrapper>
      <p
        data-testid={testIds.mermaid.lightboxHint}
        className="text-muted-foreground shrink-0 text-center text-[11px] leading-snug sm:text-xs"
      >
        Drag to pan, scroll wheel to zoom, pinch on trackpad, double-click resets. Escape to close.
      </p>
    </div>
  )
}
